import {
  getCapability,
  resolveCapabilityName,
  validateCapabilityInput,
  type AgentToolResultBlock,
  type AgentToolUseBlock,
  type CapabilityDefinition,
  type CapabilityInputValidation,
} from "@calcplan/ai-capabilities";

export type PreparedAgentToolCall =
  | {
      valid: true;
      call: AgentToolUseBlock;
      capability: CapabilityDefinition;
      repairs: ToolInputRepair[];
    }
  | {
      valid: false;
      call: AgentToolUseBlock;
      result: AgentToolResultBlock;
    };

export interface ToolInputRepair {
  path: string;
  action: "truncate" | "wrap-array" | "prefer-existing-element" | "extract-object-fields" | "restore-validated-plan-field" | "default-empty-design-resolutions" | "normalize-design-source";
}

/**
 * Valida toda chamada vinda do modelo antes de qualquer ramificação do loop.
 * capability.invoke também valida os argumentos da capacidade de destino.
 */
export function prepareAgentToolCall(
  call: AgentToolUseBlock,
  _previousInput?: unknown,
  validatedPlanDraft?: unknown,
): PreparedAgentToolCall {
  const resolution = resolveCapabilityName(call.name, { allowSemanticReadAlias: true });
  const normalizedCall = resolution && resolution.name !== call.name
    ? { ...call, name: resolution.name }
    : call;
  // A nova chamada completa é a fonte da verdade. Mesclar silenciosamente o
  // rascunho anterior fazia campos removidos pelo modelo reaparecerem e tornava
  // certos contratos impossíveis de corrigir (especialmente campos XOR).
  const normalized = normalizeSafeCapabilityInput(normalizedCall.name, normalizedCall.input);
  const restored = restoreFromValidatedPlanDraft(normalizedCall.name, normalized.input, validatedPlanDraft);
  const repaired = repairMechanicalValidationIssues(normalizedCall.name, restored.input);
  const recoveredCall = { ...normalizedCall, input: repaired.input };
  const repairs = [...normalized.repairs, ...restored.repairs, ...repaired.repairs];
  const outer = repaired.validation;
  if (!outer.valid) return invalidToolResult(recoveredCall, outer);

  // Uma validação externa bem-sucedida também comprova que a capacidade existe.
  const requested = getCapability(recoveredCall.name)!;
  if (recoveredCall.name !== "capability.invoke") {
    return { valid: true, call: { ...recoveredCall, input: outer.data }, capability: requested, repairs };
  }

  const invocation = outer.data as { name: string; arguments: unknown };
  const innerResolution = resolveCapabilityName(invocation.name, { allowSemanticReadAlias: true });
  const normalizedInvocation = innerResolution
    ? { ...invocation, name: innerResolution.name }
    : invocation;
  const normalizedInner = normalizeSafeCapabilityInput(normalizedInvocation.name, normalizedInvocation.arguments);
  const repairedInner = repairMechanicalValidationIssues(normalizedInvocation.name, normalizedInner.input);
  const inner = repairedInner.validation;
  if (!inner.valid) {
    const prefixed: CapabilityInputValidation = {
      valid: false,
      error: {
        ...inner.error,
        message: `A capacidade encontrada ${normalizedInvocation.name} recebeu argumentos inválidos. Corrija a chamada de capability.invoke e tente novamente.`,
        issues: inner.error.issues.map((issue) => ({
          ...issue,
          path: issue.path === "input" ? "arguments" : `arguments.${issue.path}`,
        })),
      },
    };
    return invalidToolResult(recoveredCall, prefixed);
  }
  const effective = getCapability(normalizedInvocation.name)!;
  return {
    valid: true,
    call: { ...recoveredCall, input: { ...normalizedInvocation, arguments: inner.data } },
    capability: effective,
    repairs: [
      ...repairs,
      ...[...normalizedInner.repairs, ...repairedInner.repairs]
        .map((repair) => ({ ...repair, path: repair.path === "input" ? "arguments" : `arguments.${repair.path}` })),
    ],
  };
}

/**
 * A planning response can be several thousand tokens long. If a provider cuts
 * a later revision midway, recover only fields that the schema proves missing
 * or malformed from the last *schema-valid* plan. This is deliberately not a
 * generic draft merge: current values win, optional omissions stay omitted and
 * XOR action contracts can never have deleted fields resurrected.
 */
function restoreFromValidatedPlanDraft(
  name: string,
  input: unknown,
  validatedPlanDraft: unknown,
): { input: unknown; repairs: ToolInputRepair[] } {
  if (name !== "task.plan.propose" || !isPlainRecord(input) || !isPlainRecord(validatedPlanDraft)) {
    return { input, repairs: [] };
  }
  const trustedValidation = validateCapabilityInput(name, validatedPlanDraft);
  if (!trustedValidation.valid || !isPlainRecord(trustedValidation.data)) return { input, repairs: [] };

  let candidate = cloneJsonValue(input) as Record<string, unknown>;
  let validation = validateCapabilityInput(name, candidate);
  if (validation.valid) return { input: candidate, repairs: [] };
  const repairs: ToolInputRepair[] = [];

  // Required top-level fields cut from the tail are safe to restore. Only
  // validation issues with an absent current value qualify.
  for (const issue of validation.error.issues) {
    const topLevel = issue.path.split(".")[0];
    if (!topLevel || candidate[topLevel] !== undefined || trustedValidation.data[topLevel] === undefined) continue;
    candidate[topLevel] = cloneJsonValue(trustedValidation.data[topLevel]);
    repairs.push({ path: topLevel, action: "restore-validated-plan-field" });
  }

  // A provider may serialize one large array item as a string while reaching
  // its output boundary. Replace that whole malformed field only when doing so
  // strictly reduces schema errors; a valid current field is never overwritten.
  validation = validateCapabilityInput(name, candidate);
  if (!validation.valid) {
    const malformedFields = [...new Set(validation.error.issues
      .filter((issue) => issue.code === "invalid_type" && issue.path.includes("."))
      .map((issue) => issue.path.split(".")[0])
      .filter((field): field is string => Boolean(field)))];
    for (const field of malformedFields) {
      if (trustedValidation.data[field] === undefined) continue;
      const before = validation.error.issues.length;
      const replacement = { ...candidate, [field]: cloneJsonValue(trustedValidation.data[field]) };
      const replacementValidation = validateCapabilityInput(name, replacement);
      const after = replacementValidation.valid ? 0 : replacementValidation.error.issues.length;
      if (after >= before) continue;
      candidate = replacement;
      validation = replacementValidation;
      repairs.push({ path: field, action: "restore-validated-plan-field" });
      if (validation.valid) break;
    }
  }

  return { input: candidate, repairs };
}

function invalidToolResult(
  call: AgentToolUseBlock,
  validation: Extract<CapabilityInputValidation, { valid: false }>,
): PreparedAgentToolCall {
  return {
    valid: false,
    call,
    result: {
      type: "tool_result",
      toolUseId: call.id,
      isError: true,
      result: {
        error: validation.error.message,
        code: validation.error.code,
        capability: validation.error.capability,
        issues: validation.error.issues,
        retryable: validation.error.retryable,
        recovery: validation.error.code === "UNKNOWN_CAPABILITY"
          ? "Use exatamente o nome de uma das capacidades oferecidas nesta chamada. Não invente ferramentas nem repita o nome inexistente."
          : "Reenvie o objeto completo da chamada, com todos os campos obrigatórios. Preserve os campos válidos, corrija os indicados e não envie apenas um patch. Não explique o erro ao projetista.",
      },
    },
  };
}

function normalizeSafeCapabilityInput(name: string, input: unknown): { input: unknown; repairs: ToolInputRepair[] } {
  if (!isPlainRecord(input)) return { input, repairs: [] };
  const normalized = cloneJsonValue(input) as Record<string, unknown>;
  const repairs: ToolInputRepair[] = [];
  if (name === "scene.placement.evaluate" && typeof normalized.elementId === "string" && typeof normalized.catalogItemId === "string") {
    // A presença de elementId torna inequívoco que a avaliação é do elemento
    // já existente. catalogItemId só é necessário antes da inserção.
    delete normalized.catalogItemId;
    repairs.push({ path: "catalogItemId", action: "prefer-existing-element" });
  }
  if (name === "task.plan.propose"
    && normalized.designResolutions === undefined
    && Array.isArray(normalized.freeVariables)
    && normalized.freeVariables.length === 0) {
    normalized.designResolutions = [];
    repairs.push({ path: "designResolutions", action: "default-empty-design-resolutions" });
  }
  if (name === "task.plan.propose") {
    for (const field of ["spatialDecisions", "decisionAuthorities", "designResolutions"] as const) {
      if (!Array.isArray(normalized[field])) continue;
      normalized[field] = normalized[field].map((rawDecision, index) => {
        if (!isPlainRecord(rawDecision)) return rawDecision;
        const source = rawDecision.source;
        if (source !== "explicit" && source !== "briefing") return rawDecision;
        repairs.push({ path: `${field}.${index}.source`, action: "normalize-design-source" });
        return { ...rawDecision, source: "user" };
      });
    }
  }
  if (name === "task.plan.propose" && !Array.isArray(normalized.steps) && typeof normalized.discoverySummary === "string") {
    const recovered = recoverMisplacedPlanFields(normalized.discoverySummary);
    if (recovered) {
      for (const [key, value] of Object.entries(recovered)) {
        if (normalized[key] === undefined || key === "discoverySummary") normalized[key] = value;
      }
      repairs.push({ path: "discoverySummary", action: "extract-object-fields" });
    }
  }
  return { input: normalized, repairs };
}

/**
 * Some providers occasionally serialize the remaining sibling fields of a
 * large tool object into the last string field. Recover only a syntactically
 * valid object fragment and let the normal Zod contract validate every value.
 */
function recoverMisplacedPlanFields(value: string): Record<string, unknown> | null {
  const fragment = value.trim();
  if (!fragment.startsWith("[") || !fragment.includes('"steps"')) return null;
  try {
    const parsed = JSON.parse(`{"discoverySummary":${fragment}}`) as unknown;
    if (!isPlainRecord(parsed) || !Array.isArray(parsed.discoverySummary) || !Array.isArray(parsed.steps)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function repairMechanicalValidationIssues(
  name: string,
  input: unknown,
): { input: unknown; repairs: ToolInputRepair[]; validation: CapabilityInputValidation } {
  let candidate = cloneJsonValue(input);
  const repairs: ToolInputRepair[] = [];
  let validation = validateCapabilityInput(name, candidate);
  for (let pass = 0; pass < 3 && !validation.valid; pass += 1) {
    let changed = false;
    for (const issue of validation.error.issues) {
      const path = issue.path === "input" ? [] : issue.path.split(".");
      const current = valueAtPath(candidate, path);
      if (issue.code === "too_big" && typeof current === "string") {
        const maximum = maximumStringLength(issue.message);
        if (maximum !== null && current.length > maximum) {
          candidate = valueWithPath(candidate, path, compactString(current, maximum));
          repairs.push({ path: issue.path, action: "truncate" });
          changed = true;
        }
      } else if (issue.code === "invalid_type" && typeof current === "string" && /expected array/i.test(issue.message)) {
        candidate = valueWithPath(candidate, path, [current]);
        repairs.push({ path: issue.path, action: "wrap-array" });
        changed = true;
      }
    }
    if (!changed) break;
    validation = validateCapabilityInput(name, candidate);
  }
  return { input: candidate, repairs, validation };
}

function maximumStringLength(message: string): number | null {
  const match = message.match(/<=\s*(\d+)\s*characters/i);
  return match ? Number(match[1]) : null;
}

function compactString(value: string, maximum: number): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= maximum) return normalized;
  if (maximum <= 1) return normalized.slice(0, maximum);
  const suffix = /\?$/.test(normalized) ? "?" : "…";
  const available = Math.max(1, maximum - suffix.length);
  const prefix = normalized.slice(0, available);
  const boundary = prefix.lastIndexOf(" ");
  const compact = boundary >= Math.floor(available * 0.6) ? prefix.slice(0, boundary) : prefix;
  return `${compact.trimEnd()}${suffix}`.slice(0, maximum);
}

function valueAtPath(root: unknown, path: string[]): unknown {
  let current = root;
  for (const segment of path) {
    if (Array.isArray(current)) current = current[Number(segment)];
    else if (isPlainRecord(current)) current = current[segment];
    else return undefined;
  }
  return current;
}

function valueWithPath(root: unknown, path: string[], value: unknown): unknown {
  if (!path.length) return value;
  const copy = cloneJsonValue(root);
  let current: unknown = copy;
  for (let index = 0; index < path.length - 1; index += 1) {
    const segment = path[index]!;
    current = Array.isArray(current)
      ? current[Number(segment)]
      : isPlainRecord(current) ? current[segment] : undefined;
  }
  const leaf = path.at(-1)!;
  if (Array.isArray(current)) current[Number(leaf)] = value;
  else if (isPlainRecord(current)) current[leaf] = value;
  return copy;
}

function cloneJsonValue<T>(value: T): T {
  if (Array.isArray(value)) return value.map((item) => cloneJsonValue(item)) as T;
  if (isPlainRecord(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, cloneJsonValue(item)])) as T;
  }
  return value;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
